import { ObjectId } from "@novorender/api";

import { ListNode, Tree } from "components/objectTree/types";
import { ModelTreeNodeData } from "features/modelTree/types";

import { binarySearch } from "./arrays";
import { SelectionSet } from "./selectionSet";

export type GroupState = "all" | "some" | "none";

// ---------------------------------------------------------------------------
// Internal: O(n) bottom-up terminal-count caches
//
// For each (ids, tree) pair we build a lazy, memoized counter function.
// The first call for a given node path triggers a depth-first traversal
// of that subtree, caching { selected, total } terminal counts for every
// node visited along the way.  Subsequent calls for any already-visited
// path are an O(1) Map lookup — subtrees that are never queried are
// never traversed.
//
// WeakMaps ensure entries are GC-collected as soon as the key objects
// (ids, tree) are replaced or otherwise become unreachable. This relies
// on callers passing new object identities when selection or tree state
// changes.
// ---------------------------------------------------------------------------

interface TerminalCounts {
    selected: number;
    total: number;
    /** True when this node or any non-terminal descendant is directly selected. */
    hasSelectedIntermediate: boolean;
    /**
     * True when the loaded view fully accounts for this subtree. When `false`
     * (a child folder's contents aren't loaded yet, or this node is only
     * partially expanded) `total` is just a lower bound.
     */
    complete: boolean;
}

const EMPTY_COUNTS: TerminalCounts = { selected: 0, total: 0, hasSelectedIntermediate: false, complete: true };

/** Count how many of a node's cached descendant ids are present in the set. */
function countSelectedDescendants(descendants: readonly ObjectId[], ids: SelectionSet): number {
    if (ids.size < Math.log2(descendants.length)) {
        // If number of selected ids is significantly smaller than the number of
        // descendants, iterate the selected ids and binary-search the (sorted)
        // descendants array instead of scanning every descendant.
        const min = descendants[0];
        const max = descendants[descendants.length - 1];
        let selected = 0;
        for (const key of ids.sortedIds) {
            if (key >= min && key <= max && binarySearch(descendants, key, (a, b) => a - b) !== -1) {
                selected++;
            }
        }
        return selected;
    }

    let selected = 0;
    for (const id of descendants) {
        if (ids.has(id)) {
            selected++;
        }
    }
    return selected;
}

/** Map terminal counts to a group state. Returns undefined when total === 0 (no data). */
function countsToGroupState(counts: TerminalCounts): GroupState | undefined {
    if (counts.total <= 0) {
        return undefined;
    }

    if (counts.complete && counts.selected === counts.total) {
        return "all";
    }

    if (counts.selected > 0 || counts.hasSelectedIntermediate) {
        return "some";
    }

    return "none";
}

/**
 * Iterative (explicit-stack) counter over the filtered tree. Recursion is
 * avoided deliberately: source models can nest tens of thousands of levels
 * deep, which overflows the JS call stack (see buildPlainCounter).
 *
 * A child contributes 1 to `total` (and possibly `selected`) when its id is in
 * the filtered set; its subtree counts are always added on top. The filtered
 * tree is self-contained (search results include all relevant nodes), so
 * counts are always `complete`.
 */
function buildFilteredCounter(
    ids: SelectionSet,
    filteredObjectIds: Set<ObjectId>,
    tree: Tree<ModelTreeNodeData>,
): (path: string) => TerminalCounts {
    const memo = new Map<string, TerminalCounts>();

    return (rootPath: string) => {
        type Frame = { path: string; expanded: boolean };
        const stack: Frame[] = [{ path: rootPath, expanded: false }];
        const active = new Set<string>();

        while (stack.length) {
            const frame = stack.at(-1)!;
            if (memo.has(frame.path)) {
                stack.pop();
                continue;
            }

            const node = tree[frame.path];
            if (!node || !node.children || node.children.length === 0) {
                memo.set(frame.path, EMPTY_COUNTS);
                stack.pop();
                continue;
            }

            if (!frame.expanded) {
                frame.expanded = true;
                active.add(frame.path);
                for (let index = node.children.length - 1; index >= 0; index--) {
                    const childPath = node.children[index];
                    if (!memo.has(childPath) && !active.has(childPath)) {
                        stack.push({ path: childPath, expanded: false });
                    }
                }
                continue;
            }

            let selected = 0;
            let total = 0;
            let hasSelectedIntermediate = ids.has(node.data.object.id);
            for (const childPath of node.children) {
                const child = tree[childPath];
                if (!child) {
                    continue;
                }

                if (filteredObjectIds.has(child.data.object.id)) {
                    total++;
                    if (ids.has(child.data.object.id)) {
                        selected++;
                    }
                }

                const childCounts = memo.get(childPath) ?? EMPTY_COUNTS;
                selected += childCounts.selected;
                total += childCounts.total;
                hasSelectedIntermediate ||= childCounts.hasSelectedIntermediate;
            }
            active.delete(frame.path);
            memo.set(frame.path, { selected, total, hasSelectedIntermediate, complete: true });
            stack.pop();
        }

        return memo.get(rootPath) ?? EMPTY_COUNTS;
    };
}

function buildPlainCounter(ids: SelectionSet, tree: Tree<ModelTreeNodeData>): (path: string) => TerminalCounts {
    const memo = new Map<string, TerminalCounts>();

    return (rootPath: string) => {
        type Frame = { path: string; expanded: boolean };
        const stack: Frame[] = [{ path: rootPath, expanded: false }];
        const active = new Set<string>();

        while (stack.length) {
            const frame = stack.at(-1)!;
            if (memo.has(frame.path)) {
                stack.pop();
                continue;
            }

            const node = tree[frame.path];
            if (!node) {
                memo.set(frame.path, EMPTY_COUNTS);
                stack.pop();
                continue;
            }

            const selfSelected = ids.has(node.data.object.id);
            const descendants = node.data.object.descendants;
            const hasLoadedChildren = node.children !== undefined;
            const hasPartialChildren = node.hasMoreChildren === true || !!node.data.childIterator;

            // Terminal: leaf or folder whose children haven't been loaded yet.
            if (node.isLeaf || !hasLoadedChildren) {
                if (!node.isLeaf && descendants && descendants.length > 0) {
                    memo.set(frame.path, {
                        selected: countSelectedDescendants(descendants, ids),
                        total: descendants.length,
                        hasSelectedIntermediate: selfSelected,
                        complete: true,
                    });
                } else if (!node.isLeaf) {
                    memo.set(frame.path, {
                        selected: 0,
                        total: 0,
                        hasSelectedIntermediate: selfSelected,
                        complete: node.children !== undefined || descendants !== undefined,
                    });
                } else {
                    memo.set(frame.path, {
                        selected: selfSelected ? 1 : 0,
                        total: 1,
                        hasSelectedIntermediate: false,
                        complete: true,
                    });
                }
                stack.pop();
                continue;
            }

            if (!frame.expanded) {
                frame.expanded = true;
                active.add(frame.path);
                for (let index = node.children!.length - 1; index >= 0; index--) {
                    const childPath = node.children![index];
                    if (!memo.has(childPath) && !active.has(childPath)) {
                        stack.push({ path: childPath, expanded: false });
                    }
                }
                continue;
            }

            let selected = 0;
            let total = 0;
            let hasSelectedIntermediate = selfSelected;
            let complete = !hasPartialChildren;
            for (const childPath of node.children!) {
                const counts = memo.get(childPath) ?? EMPTY_COUNTS;
                selected += counts.selected;
                total += counts.total;
                hasSelectedIntermediate ||= counts.hasSelectedIntermediate;
                complete &&= counts.complete;
            }
            active.delete(frame.path);
            memo.set(
                frame.path,
                !complete && descendants && descendants.length > 0
                    ? {
                          selected: countSelectedDescendants(descendants, ids),
                          total: descendants.length,
                          hasSelectedIntermediate,
                          complete: true,
                      }
                    : { selected, total, hasSelectedIntermediate, complete },
            );
            stack.pop();
        }

        return memo.get(rootPath) ?? EMPTY_COUNTS;
    };
}

// ---------------------------------------------------------------------------
// Plain (unfiltered) cache:  ids → tree → counter function
// ---------------------------------------------------------------------------

const plainCountsByIds = new WeakMap<
    SelectionSet,
    WeakMap<Tree<ModelTreeNodeData>, (path: string) => TerminalCounts>
>();

function getPlainCounts(ids: SelectionSet, tree: Tree<ModelTreeNodeData>): (path: string) => TerminalCounts {
    let byTree = plainCountsByIds.get(ids);
    if (!byTree) {
        byTree = new WeakMap();
        plainCountsByIds.set(ids, byTree);
    }

    const cached = byTree.get(tree);
    if (cached) {
        return cached;
    }

    const counter = buildPlainCounter(ids, tree);

    // Keep computation lazy: counts are memoized per requested path,
    // avoiding an unconditional full-tree traversal when the cache is built.
    byTree.set(tree, counter);
    return counter;
}

// ---------------------------------------------------------------------------
// Filtered cache:  ids → filteredObjectIds → tree → counter function
// ---------------------------------------------------------------------------

const filteredCountsByIds = new WeakMap<
    SelectionSet,
    WeakMap<Set<ObjectId>, WeakMap<Tree<ModelTreeNodeData>, (path: string) => TerminalCounts>>
>();

function getFilteredCounts(
    ids: SelectionSet,
    filteredObjectIds: Set<ObjectId>,
    tree: Tree<ModelTreeNodeData>,
): (path: string) => TerminalCounts {
    let byFiltered = filteredCountsByIds.get(ids);
    if (!byFiltered) {
        byFiltered = new WeakMap();
        filteredCountsByIds.set(ids, byFiltered);
    }

    let byTree = byFiltered.get(filteredObjectIds);
    if (!byTree) {
        byTree = new WeakMap();
        byFiltered.set(filteredObjectIds, byTree);
    }

    const cached = byTree.get(tree);
    if (cached) {
        return cached;
    }

    const counter = buildFilteredCounter(ids, filteredObjectIds, tree);

    byTree.set(tree, counter);
    return counter;
}

/**
 * Resolve a non-leaf folder's display state from its terminal counts.
 * Returns `undefined` only when the view is still incomplete (children loading).
 */
function folderStateFromCounts(ids: SelectionSet, nodeId: ObjectId, counts: TerminalCounts): GroupState | undefined {
    const state = countsToGroupState(counts);

    if (state) {
        return state;
    }

    if (counts.complete) {
        if (ids.has(nodeId)) {
            return "all";
        }

        return counts.hasSelectedIntermediate ? "some" : "none";
    }

    // Incomplete view, but a directly-selected intermediate is known to exist in the
    // subtree: report partial instead of unknown. Otherwise a folder whose only hidden
    // content is an unresolved child folder would fall back to "none" while its own
    // ancestors (whose aggregated totals are non-zero) report "some" — an inconsistent
    // chain (countsToGroupState already returns "some" for this whenever total > 0).
    return counts.hasSelectedIntermediate ? "some" : undefined;
}

export function getGroupStateForIdSet(
    ids: SelectionSet,
    item: ListNode<ModelTreeNodeData>,
    tree: Tree<ModelTreeNodeData>,
): GroupState {
    if (item.nodeGroup) {
        let some = false;
        let all = true;
        for (const node of item.nodeGroup) {
            const inSet = ids.has(node.data.object.id);
            some ||= inSet;
            all &&= inSet;
        }
        return all ? "all" : some ? "some" : "none";
    }

    const node = item.node;

    if (!node.isLeaf && node.children && node.children.length > 0) {
        const state = folderStateFromCounts(ids, node.data.object.id, getPlainCounts(ids, tree)(node.path));
        if (state) {
            return state;
        }
    }

    if (item.nodes) {
        const selectedCount = item.nodes.reduce((acc, n) => acc + (ids.has(n.data.object.id) ? 1 : 0), 0);
        if (selectedCount === item.nodes.length) {
            return "all";
        }
        if (selectedCount > 0) {
            return "some";
        }
        return "none";
    }

    // Children not loaded yet: derive folder state from known descendants.
    if (!node.isLeaf) {
        const state = folderStateFromCounts(ids, node.data.object.id, getPlainCounts(ids, tree)(node.path));
        if (state) {
            return state;
        }
    }

    // For leaf nodes, the node's own ID directly represents renderable geometry
    // so its presence in the set is authoritative.
    // For non-leaf (folder) nodes with no loaded children or descendants, the
    // folder's own ID being in the set is not sufficient to determine full
    // subtree coverage — folder IDs don't have renderable geometry so hiding
    // them doesn't hide anything in 3D. Report "some" (partial/unknown) to
    // avoid incorrectly showing the EyeOff icon for visible objects.
    if (ids.has(node.data.object.id)) {
        return node.isLeaf ? "all" : "some";
    }
    return "none";
}

/**
 * Filter-aware version of getGroupStateForIdSet.
 * For folders, checks if all descendants in the filtered tree are selected.
 */
export function getGroupStateForFilteredTree(
    ids: SelectionSet,
    item: ListNode<ModelTreeNodeData>,
    filteredObjectIds: Set<number>,
    tree: Tree<ModelTreeNodeData>,
): GroupState {
    // Handle node groups (similar leaf nodes grouped together)
    if (item.nodeGroup) {
        let some = false;
        let all = true;
        for (const node of item.nodeGroup) {
            const inSet = ids.has(node.data.object.id);
            some ||= inSet;
            all &&= inSet;
        }
        return all ? "all" : some ? "some" : "none";
    }

    // Handle collapsed single-child chains
    if (item.nodes) {
        // In filtered mode, check descendants of collapsed nodes instead of just the nodes themselves
        const getFilteredCount = getFilteredCounts(ids, filteredObjectIds, tree);
        let allSelected = true;
        let someSelected = false;

        for (const n of item.nodes) {
            if (!n.isLeaf && n.children && n.children.length > 0) {
                const counts = getFilteredCount(n.path);
                if (counts.total > 0) {
                    allSelected &&= counts.selected === counts.total;
                    someSelected ||= counts.selected > 0 || counts.hasSelectedIntermediate;
                    continue;
                }
            }

            // Leaf or no filtered descendants in this node — check the node itself.
            const nodeSelected = ids.has(n.data.object.id);
            allSelected &&= nodeSelected;
            someSelected ||= nodeSelected;
        }

        return allSelected ? "all" : someSelected ? "some" : "none";
    }

    // For folders with children, check all descendants in the filtered tree
    const node = item.node;
    if (!node.isLeaf && node.children && node.children.length > 0) {
        const counts = getFilteredCounts(ids, filteredObjectIds, tree)(node.path);
        if (counts.total === 0) {
            // No filtered descendants — just check the node itself
            return ids.has(node.data.object.id) ? "all" : "none";
        }

        return countsToGroupState(counts) ?? "none";
    }

    // Leaf node - just check itself
    return ids.has(item.node.data.object.id) ? "all" : "none";
}

/**
 * Derives the minimal set of "top-level selected" nodes from the current
 * highlighted IDs and the loaded object tree.
 *
 * Walk strategy:
 *   "all"  → add the node as a top-level selection and prune (children covered)
 *   "some" → recurse into loaded children to find deeper fully-selected sub-roots
 *   "none" → skip
 *
 * For collapsed folder nodes with a `descendants` metadata array the existing
 * getPlainCounts logic already resolves counts without requiring the subtree to
 * be expanded, so canvas-selected objects whose parent is loaded but not
 * expanded are handled correctly.
 *
 * If an ancestor node is not in the tree at all (pure canvas-only selection,
 * tree widget never opened) this function returns an empty record and the
 * caller should fall back to the slower batchedPropertySearch path.
 */
export function deriveTopLevelSelections(
    ids: SelectionSet,
    tree: Tree<ModelTreeNodeData>,
): Record<number, { path: string }> {
    const result: Record<number, { path: string }> = {};
    const root = tree[""];
    if (!root?.children) {
        return result;
    }

    const compute = getPlainCounts(ids, tree);

    function walk(path: string): void {
        const counts = compute(path);
        const state = countsToGroupState(counts);
        if (!state || state === "none") {
            return;
        }
        const node = tree[path];
        if (!node) {
            return;
        }
        if (state === "all") {
            result[node.data.object.id] = { path: node.path };
            return; // prune — all children are covered by this node
        }
        for (const childPath of node.children ?? []) {
            walk(childPath);
        }
    }

    for (const childPath of root.children) {
        walk(childPath);
    }
    return result;
}
